import React, { useState, useContext, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserAuthContext } from '../../UserAuthContext';
import { useLanguage } from '../../LanguageContext';

function InicioSesion() {
  const { login } = useContext(UserAuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPanel, setShowPanel] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const loginLockRef = useRef(false);

  // Other parts of the app (cart/product actions that detect an expired
  // session) send the user home and set this flag so the login panel opens
  // automatically here, instead of dropping them on the signup form.
  useEffect(() => {
    try {
      if (sessionStorage.getItem('valley_open_auth') === '1') {
        sessionStorage.removeItem('valley_open_auth');
        setIsLogin(true);
        setShowPanel(true);
      }
    } catch {
      /* sessionStorage unavailable, non-fatal */
    }
  }, []);

  const handleAuthClick = () => {
    setShowPanel(!showPanel);
    setErrorMessage('');
  };

  const handleLoginTabClick = () => {
    setIsLogin(true);
    setErrorMessage('');
    setEmail('');
    setPassword('');
  };

  const handleSignUpTabClick = () => {
    setIsLogin(false);
    setErrorMessage('');
    setEmail('');
    setPassword('');
  };

  const handleLoginSubmit = async (event) => {
    event?.preventDefault();
    if (loginLockRef.current) return;

    setErrorMessage('');

    const normalizedEmail = email.trim().toLowerCase();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail) || !password) {
      setErrorMessage(t('Enter a valid email address and password', 'Ingrese un correo electrónico y una contraseña válidos'));
      return;
    }

    loginLockRef.current = true;
    setSubmitting(true);
    try {
      const res = await fetch('https://valleyapi-dr9y.onrender.com/api/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: normalizedEmail, password }),
      });

      const data = await res.json();

      if (res.ok) {
        const { token, refreshToken, user } = data;
        login(user, token, refreshToken);
        // Honor a returnTo carried on the current route's state (the same
        // pattern Signup.js uses), so logging in from here doesn't strand
        // the user back on the homepage when they were mid-checkout/cart.
        const destination = location.state?.returnTo || '/';
        navigate(destination, { replace: true });
      } else {
        if (res.status === 400) {
          setErrorMessage(data.details ? data.details.map(err => err.msg).join(', ') : data.error || 'Invalid input');
        } else if (res.status === 429) {
          setErrorMessage(t('Too many login attempts, please try again later', 'Demasiados intentos de inicio de sesión; inténtelo más tarde'));
        } else if (res.status === 401) {
          setErrorMessage(t('Invalid credentials', 'Credenciales no válidas'));
        } else if (res.status === 403) {
          setErrorMessage(t('Please verify your email before logging in', 'Verifique su correo electrónico antes de iniciar sesión'));
        } else {
          setErrorMessage(data.error || t('Login failed', 'No se pudo iniciar sesión'));
        }
      }
    } catch (err) {
      console.error('❌ Login: Fetch error:', err.message);
      setErrorMessage(t('Server connection error', 'Error de conexión con el servidor'));
    } finally {
      loginLockRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div style={containerStyles}>
      <span onClick={handleAuthClick} style={authTextStyles}>
        {t('LOG IN / SIGN UP', 'INGRESAR / REGISTRARSE')}
      </span>

      {showPanel && (
        <div style={panelStyles}>
          <div style={tabsStyles}>
            <button
              onClick={handleLoginTabClick}
              style={{
                ...tabStyles,
                backgroundColor: isLogin ? '#f9f9f9' : 'transparent',
                borderBottom: isLogin ? '2px solid #e0e0e0' : 'none',
                color: isLogin ? '#333' : '#777',
              }}
            >
              {t('LOG IN', 'INGRESAR')}
            </button>
            <button
              onClick={handleSignUpTabClick}
              style={{
                ...tabStyles,
                backgroundColor: !isLogin ? '#f9f9f9' : 'transparent',
                borderBottom: !isLogin ? '2px solid #e0e0e0' : 'none',
                color: !isLogin ? '#333' : '#777',
              }}
            >
              {t('SIGN UP', 'REGISTRARSE')}
            </button>
          </div>

          <div style={formContainerStyles}>
            {isLogin ? (
              <form onSubmit={handleLoginSubmit}>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={inputStyles}
                  required
                />
                <input
                  type="password"
                  placeholder={t('Password', 'Contraseña')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  style={inputStyles}
                  required
                />
                {errorMessage && <p style={errorStyles}>{errorMessage}</p>}
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    ...loginButtonStyles,
                  }}
                >
                  {submitting ? t('LOGGING IN...', 'INGRESANDO...') : t('LOG IN', 'INGRESAR')}
                </button>
                <a href="/forgot-password" className="forgot-password-link" style={forgotPasswordButtonStyles}>
                  {t('Forgot Password?', '¿Olvidó su contraseña?')}
                </a>
              </form>
            ) : (
              <div style={{ textAlign: 'center', padding: '1rem' }}>
                <button
                  onClick={() => navigate('/signup', location.state?.returnTo ? { state: { returnTo: location.state.returnTo } } : undefined)}
                  style={{
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '5px',
                    fontSize: '16px',
                    cursor: 'pointer',
                  }}
                >
                  {t('Create Your Account Now!', '¡Cree su cuenta ahora!')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const containerStyles = {
  position: 'relative',
  display: 'inline-block',
  padding: '5px 0px',
};

const authTextStyles = {
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#fff',
  backgroundColor: '#e67e22',
  padding: '5px 10px',
  borderRadius: '5px',
};

const panelStyles = {
  position: 'absolute',
  top: '100%',
  right: 0,
  backgroundColor: '#fff',
  border: '1px solid #ccc',
  borderRadius: '5px',
  boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
  zIndex: 10,
  width: '300px',
};

const tabsStyles = {
  display: 'flex',
  borderBottom: '1px solid #e0e0e0',
};

const tabStyles = {
  flex: 1,
  padding: '10px 15px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 'bold',
  color: '#777',
};

const formContainerStyles = {
  padding: '20px',
};

const inputStyles = {
  width: '100%',
  padding: '10px',
  marginBottom: '15px',
  borderRadius: '4px',
  border: '1px solid #ccc',
  boxSizing: 'border-box',
  fontSize: '14px',
};

const loginButtonStyles = {
  backgroundColor: '#e67e22',
  color: 'white',
  padding: '10px 15px',
  border: 'none',
  borderRadius: '4px',
  fontSize: '14px',
  width: '100%',
  marginBottom: '10px',
};

const forgotPasswordButtonStyles = {
  background: 'none',
  border: 'none',
  color: '#007bff',
  cursor: 'pointer',
  fontSize: '12px',
  padding: 0,
  textDecoration: 'underline',
  alignSelf: 'flex-start',
};

const errorStyles = {
  color: 'red',
  marginBottom: '10px',
  fontSize: '12px',
};

export default InicioSesion;
